import type { ChatModelRunResult } from '@assistant-ui/react';

import { isRefused } from '../api/andiClient';
import type {
  Chat, ChoiceOption, StreamEndStatus, StreamFrame,
} from '../api/types';
import { isReplyInterrupted } from '../chatState';
import { FOLLOW_REPLY_FOR_MS } from '../constants';

import { emptyReply, type ReplyItem } from './messageItem';
import { savedReply } from './transcript';

/** A reply as its frames arrive. */
export type ReplyDraft = {
  events: string[];
  variations: string[];
  reply: string;
  options: ChoiceOption[];
  question: string;
  locked: boolean;
  /** Where the reply was saved, from `done`: lets it be copied and rated as soon as it lands. */
  index: number | null;
  runId: string;
  /** The `done` frame arrived: the reply is complete and saved. */
  done: boolean;
  /** An `error` frame arrived: the reply broke off. It shows as interrupted, never as the error. */
  broke: boolean;
  /** The stream the reply is written on, from its first frame: what a dropped stream is followed on. */
  streamId: string | null;
  /** How the stream ended; null when it closed without saying. */
  endStatus: StreamEndStatus | null;
};

export const newDraft = (events: string[] = []): ReplyDraft => ({
  events: [...events],
  variations: [],
  reply: '',
  options: [],
  question: '',
  locked: false,
  index: null,
  runId: '',
  done: false,
  broke: false,
  streamId: null,
  endStatus: null,
});

export function applyFrame(draft: ReplyDraft, frame: StreamFrame): void {
  switch (frame.type) {
    case 'event':
      draft.events = [...draft.events, frame.text];
      draft.variations = frame.variations ?? [];
      break;
    case 'token':
      draft.reply += frame.text;
      break;
    case 'error':
      draft.broke = true;
      break;
    case 'stream':
      draft.streamId = frame.stream_id;
      break;
    case 'stream_end':
      draft.endStatus = frame.status;
      break;
    case 'done':
      // `done` carries the saved reply. Tokens are only a preview of it: a question's text often streams no
      // tokens at all, and a resumed stream can replay a thin tail of them.
      draft.options = frame.options ?? [];
      draft.question = frame.options_question ?? '';
      draft.locked = Boolean(frame.locked);
      if (frame.reply) draft.reply = frame.reply;
      draft.index = frame.index ?? null;
      draft.runId = frame.run_id ?? '';
      draft.done = true;
      break;
    case 'title':
      break; // the chat's name, for the chat list: not part of the reply
  }
}

/** The draft as assistant-ui's run result, with our reply item on its metadata. */
export function draftResult(draft: ReplyDraft, running: boolean, prompt: string, interrupted = false): ChatModelRunResult {
  const item: ReplyItem = emptyReply({
    events: draft.events,
    variations: draft.variations,
    reply: draft.reply,
    options: draft.options,
    question: draft.question,
    locked: draft.locked,
    index: draft.index,
    runId: draft.runId,
    running,
    prompt,
    interrupted,
  });
  return {
    content: draft.reply ? [{ type: 'text', text: draft.reply }] : [],
    metadata: { custom: { item } },
  };
}

type Follow = {
  prompt: string;
  /** The stream the reply is written on, when it's known; otherwise the chat's state names it. */
  streamId: string | null;
  /** The stream's frames from its start, then the live ones until it ends. */
  resume: (streamId: string) => AsyncIterable<StreamFrame>;
  /** The chat's state now, or null when it couldn't be read. */
  latestChat: () => Promise<Chat | null>;
  /** The reply ended: refresh what depends on the chat's state. */
  onEnded: () => void;
  stop: () => boolean;
};

const sleep = (ms: number) => new Promise((resolve) => {
  setTimeout(resolve, ms);
});

/**
 * Follows a reply the server is writing until it is answered or interrupted, the only two ways it ends for the
 * builder. A dropped stream is not an ending: check the chat's state, then reconnect. A reply whose end can't
 * be seen within FOLLOW_REPLY_FOR_MS counts as interrupted.
 */
export async function* followReply({
  prompt, streamId: knownStreamId, resume, latestChat, onEnded, stop,
}: Follow): AsyncGenerator<ChatModelRunResult> {
  const started = Date.now();
  let streamId = knownStreamId;
  let draft = newDraft();
  yield draftResult(draft, true, prompt);

  for (let attempt = 0; Date.now() - started < FOLLOW_REPLY_FOR_MS; attempt += 1) {
    draft = newDraft(); // the stream replays the reply from its start
    // A refused call (not allowed, or no such stream) has nothing more coming; anything else dropped. Either
    // way the chat's state says what happened.
    let refused = false;
    try {
      if (streamId) {
        for await (const frame of resume(streamId)) {
          applyFrame(draft, frame);
          yield draftResult(draft, true, prompt);
        }
      }
    } catch (error) {
      refused = isRefused(error);
    }
    if (stop()) return;
    if (draft.done) {
      onEnded();
      yield draftResult(draft, false, prompt);
      return;
    }
    if (draft.broke || draft.endStatus === 'failed' || draft.endStatus === 'interrupted') {
      onEnded();
      yield draftResult(draft, false, prompt, true);
      return;
    }
    const chat = await latestChat();
    const saved = chat && savedReply(chat);
    if (saved) {
      yield saved;
      return;
    }
    // Over with no answer saved: interrupted. Otherwise it is still being written: reconnect.
    if (refused || draft.endStatus === 'completed' || (chat && isReplyInterrupted(chat))) {
      yield draftResult(draft, false, prompt, true);
      return;
    }
    streamId = chat?.turn?.stream_id || streamId;
    await sleep(Math.min(1_000 * 2 ** attempt, 5_000));
  }
  yield draftResult(draft, false, prompt, true);
}
